import { Check, Copy } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { StatusBanner } from '../../shared/ui/StatusBanner';

import s from './CredentialCard.module.css';

export interface StudentCredential {
  name: string;
  username: string;
  password: string;
  loginUrl: string;
}

interface Props {
  credential: StudentCredential;
}

type Field = 'loginUrl' | 'username' | 'password' | 'all';

/**
 * One-time display of a freshly-minted student login. The password is shown
 * ONCE (Auth stores only the hash), so the copy affordances and the "save it
 * now" warning are the point of this component, not decoration.
 */
export function CredentialCard({ credential }: Props): JSX.Element {
  const [copied, setCopied] = useState<Field | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const copy = useCallback(async (field: Field, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(field);
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(null), 1600);
    } catch {
      // Clipboard unavailable (insecure context / denied) — leave the value on
      // screen so the institute can copy it manually.
    }
  }, []);

  const allText =
    `Login link: ${credential.loginUrl}\n` +
    `Username: ${credential.username}\n` +
    `Password: ${credential.password}`;

  return (
    <div className={s.card}>
      <StatusBanner tone="warning">
        Save or share these now — the password is shown <strong>once</strong> and can’t be viewed
        again. You can reset it later if it’s lost.
      </StatusBanner>

      <dl className={s.rows}>
        <CredentialRow
          label="Login link"
          value={credential.loginUrl}
          mono
          copied={copied === 'loginUrl'}
          onCopy={() => void copy('loginUrl', credential.loginUrl)}
        />
        <CredentialRow
          label="Username"
          value={credential.username}
          mono
          copied={copied === 'username'}
          onCopy={() => void copy('username', credential.username)}
        />
        <CredentialRow
          label="Password"
          value={credential.password}
          mono
          emphasize
          copied={copied === 'password'}
          onCopy={() => void copy('password', credential.password)}
        />
      </dl>

      <button
        type="button"
        className={s.copyAll}
        onClick={() => void copy('all', allText)}
        aria-label="Copy login link, username and password"
      >
        {copied === 'all' ? (
          <Check size={15} aria-hidden="true" />
        ) : (
          <Copy size={15} aria-hidden="true" />
        )}
        {copied === 'all' ? 'Copied' : 'Copy all'}
      </button>
    </div>
  );
}

interface RowProps {
  label: string;
  value: string;
  mono?: boolean;
  emphasize?: boolean;
  copied: boolean;
  onCopy: () => void;
}

function CredentialRow({ label, value, mono, emphasize, copied, onCopy }: RowProps): JSX.Element {
  return (
    <div className={s.row}>
      <dt className={s.label}>{label}</dt>
      <dd className={s.valueCell}>
        <span
          className={`${s.value ?? ''} ${mono ? (s.mono ?? '') : ''} ${
            emphasize ? (s.emphasize ?? '') : ''
          }`.trim()}
        >
          {value}
        </span>
        <button
          type="button"
          className={s.copyBtn}
          onClick={onCopy}
          aria-label={copied ? `${label} copied` : `Copy ${label.toLowerCase()}`}
        >
          {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
          <span className={s.copyText}>{copied ? 'Copied' : 'Copy'}</span>
        </button>
      </dd>
    </div>
  );
}
